import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { FaSave, FaTimes, FaEye } from 'react-icons/fa';
import { CreateNewsData, UpdateNewsData, createNews, updateNews } from '../api/news';
import { uploadImage } from '../api/upload';
import type { News } from '../api/types';

const EditorContainer = styled.div`
  background: white;
  border-radius: ${props => props.theme.borderRadius.lg};
  box-shadow: ${props => props.theme.shadows.lg};
  padding: ${props => props.theme.spacing.xxl};
  max-width: 1200px;
  margin: 0 auto;
`;

const EditorHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.xl};
  padding-bottom: ${props => props.theme.spacing.lg};
  border-bottom: 2px solid ${props => props.theme.colors.border};
`;

const EditorTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  background: ${props => {
    if (props.$variant === 'primary') return props.theme.colors.primary;
    if (props.$variant === 'danger') return '#e74c3c';
    return props.theme.colors.background;
  }};
  color: ${props => props.$variant ? 'white' : props.theme.colors.text};
  border: ${props => props.$variant ? 'none' : `2px solid ${props.theme.colors.border}`};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FormGroup = styled.div`
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const Label = styled.label`
  display: block;
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const Input = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing.md};
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 1rem;
  transition: border-color ${props => props.theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: ${props => props.theme.spacing.md};
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 1rem;
  transition: border-color ${props => props.theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const QuillWrapper = styled.div`
  .ql-container {
    min-height: 400px;
    font-size: 1rem;
    border-radius: 0 0 ${props => props.theme.borderRadius.md} ${props => props.theme.borderRadius.md};
  }

  .ql-toolbar {
    border-radius: ${props => props.theme.borderRadius.md} ${props => props.theme.borderRadius.md} 0 0;
    background: ${props => props.theme.colors.background};
  }

  .ql-editor {
    min-height: 400px;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  font-size: 1rem;
  color: ${props => props.theme.colors.text};
  cursor: pointer;
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  padding: ${props => props.theme.spacing.md};
  background: #fee;
  border-radius: ${props => props.theme.borderRadius.md};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

interface NewsEditorProps {
  newsItem?: News;
  onSave: () => void;
  onCancel: () => void;
}

const quillFormats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'list', 'bullet',
  'align',
  'link', 'image'
];

const NewsEditor: React.FC<NewsEditorProps> = ({ newsItem, onSave, onCancel }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('news');
  const [isPublished, setIsPublished] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const quillRef = useRef<ReactQuill>(null);

  // 커스텀 이미지 핸들러
  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      // 파일 크기 검증 (10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('파일 크기는 10MB 이하여야 합니다.');
        return;
      }

      try {
        const token = localStorage.getItem('adminToken');
        if (!token) {
          alert('인증 토큰이 없습니다. 다시 로그인해주세요.');
          return;
        }

        // 이미지 업로드
        const imageUrl = await uploadImage(file, token);

        // 전체 URL로 변환 (백엔드 서버 주소 포함)
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
        const fullImageUrl = imageUrl.startsWith('http') ? imageUrl : `${API_BASE_URL}${imageUrl}`;

        // Quill 에디터에 이미지 삽입
        const quill = quillRef.current?.getEditor();
        if (quill) {
          const range = quill.getSelection(true);
          quill.insertEmbed(range.index, 'image', fullImageUrl);
          quill.setSelection(range.index + 1, 0);
        }
      } catch (err) {
        alert(err instanceof Error ? err.message : '이미지 업로드에 실패했습니다.');
      }
    };
  }, []);

  // Quill 모듈 설정 (useMemo로 최적화)
  const quillModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'align': [] }],
        ['link', 'image'],
        ['clean']
      ],
      handlers: {
        image: imageHandler
      }
    }
  }), [imageHandler]);

  // 수정 모드일 때 기존 데이터 로드
  useEffect(() => {
    if (newsItem) {
      setTitle(newsItem.title);
      setContent(newsItem.content);
      setCategory(newsItem.category);
      setIsPublished(newsItem.isPublished);
    }
  }, [newsItem]);

  // HTML에서 첫 번째 이미지 URL 추출
  const extractFirstImage = (html: string): string | undefined => {
    const imgMatch = html.match(/<img[^>]+src="([^">]+)"/);
    return imgMatch ? imgMatch[1] : undefined;
  };

  // HTML 태그 제거 후 앞 150자 추출
  const generateExcerpt = (html: string): string => {
    const text = html.replace(/<[^>]*>/g, '').trim();
    return text.substring(0, 150) + (text.length > 150 ? '...' : '');
  };

  const handleSubmit = async (publish: boolean) => {
    setError('');
    setLoading(true);

    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        setError('인증 토큰이 없습니다. 다시 로그인해주세요.');
        return;
      }

      // 첫 번째 이미지를 자동으로 썸네일로 사용
      const thumbnailUrl = extractFirstImage(content);
      const excerpt = generateExcerpt(content);

      const data: CreateNewsData | UpdateNewsData = {
        category,
        thumbnailUrl,
        isPublished: publish,
        translations: {
          ko: {
            title,
            excerpt,
            content,
          },
        },
        ...(newsItem && { id: newsItem.id }),
      };

      if (newsItem) {
        await updateNews(data as UpdateNewsData, token);
      } else {
        await createNews(data, token);
      }

      onSave();
    } catch (err) {
      setError(err instanceof Error ? err.message : '저장에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <EditorContainer>
      <EditorHeader>
        <EditorTitle>{newsItem ? '뉴스 수정' : '새 뉴스 작성'}</EditorTitle>
        <ButtonGroup>
          <Button onClick={() => handleSubmit(false)} disabled={loading}>
            <FaSave />
            임시저장
          </Button>
          <Button $variant="primary" onClick={() => handleSubmit(true)} disabled={loading}>
            <FaEye />
            게시하기
          </Button>
          <Button $variant="danger" onClick={onCancel} disabled={loading}>
            <FaTimes />
            취소
          </Button>
        </ButtonGroup>
      </EditorHeader>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <FormGroup>
        <Label>카테고리</Label>
        <Select value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="news">뉴스</option>
          <option value="press">보도자료</option>
          <option value="notice">공지사항</option>
        </Select>
      </FormGroup>

      <FormGroup>
        <Label>제목 *</Label>
        <Input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="뉴스 제목을 입력하세요"
          required
        />
      </FormGroup>

      <FormGroup>
        <Label>본문 *</Label>
        <QuillWrapper>
          <ReactQuill
            ref={quillRef}
            value={content}
            onChange={setContent}
            modules={quillModules}
            formats={quillFormats}
            placeholder="본문을 입력하세요... (이미지 버튼을 클릭하여 파일을 업로드하세요. 첫 번째 이미지가 자동으로 썸네일로 사용됩니다)"
          />
        </QuillWrapper>
      </FormGroup>

      <CheckboxGroup>
        <Checkbox
          type="checkbox"
          id="published"
          checked={isPublished}
          onChange={(e) => setIsPublished(e.target.checked)}
        />
        <CheckboxLabel htmlFor="published">
          즉시 게시
        </CheckboxLabel>
      </CheckboxGroup>
    </EditorContainer>
  );
};

export default NewsEditor;
